import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  ArrowUpRight,
  CalendarDays,
  CheckCheck,
  ClipboardCopy,
  FileCheck2,
  Loader2,
  RefreshCw,
  Users,
  X,
} from 'lucide-react';
import type { Publication, State } from '../shared/model';
import type { PublicationPackage } from '../shared/campaigns';
import type {
  DestinationCollaboration,
  DestinationDistribution,
  PackageDestination,
} from '../shared/distribution';
import { PublishQueue, publicationOrigin } from './PublishQueue';
import './distribution.css';

interface Workspace {
  packages: PublicationPackage[];
  destinations: DestinationDistribution[];
  collaborations: DestinationCollaboration[];
  publications: Publication[];
}
interface Props {
  campaignId: string;
  initialPackageId?: string;
  data: State;
  refresh: () => Promise<unknown>;
}
type Editor =
  | { kind: 'state'; item: DestinationDistribution; destination: PackageDestination }
  | { kind: 'confirm'; item: DestinationDistribution; destination: PackageDestination }
  | {
      kind: 'collaboration';
      item: DestinationDistribution;
      destination: PackageDestination;
      collaboration: DestinationCollaboration;
    };
const distributionLabels: Record<DestinationDistribution['state'], string> = {
  nao_solicitada: 'Não solicitada',
  pronta: 'Pronta',
  agendada_painel: 'Planejada no FLG',
  agendada_provedor: 'Agendada no provedor',
  em_envio: 'Em envio',
  confirmada: 'Confirmada',
  falhou: 'Falhou',
  cancelada: 'Cancelada',
  desconhecido: 'Resultado desconhecido',
};
const collaborationLabels: Record<DestinationCollaboration['status'], string> = {
  nao_solicitada: 'Não solicitada',
  planejada: 'Planejada',
  convite_enviado: 'Convite enviado',
  aceita: 'Aceita',
  recusada: 'Recusada',
  desconhecida: 'Desconhecida',
};
const observedCollaborationStates = ['convite_enviado', 'aceita', 'recusada', 'desconhecida'];
const packageLabels: Record<PublicationPackage['status'], string> = {
  aprovada: 'Aprovado',
  em_revisao: 'Em revisão',
  ajustes: 'Ajustes solicitados',
  recusada: 'Recusado',
  substituida: 'Substituído',
};
const dayLabel = (value: string) =>
  value
    ? new Date(`${value.slice(0, 10)}T12:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : 'Sem data';
const stamp = (value: string) =>
  value
    ? new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    : 'Sem horário confirmado';
const text = (form: FormData, name: string) => String(form.get(name) ?? '').trim();
const safeUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
};
const accountKey = (value: string) => value.trim().replace(/^@/, '').toLowerCase();
const sameDestination = (
  publication: Publication,
  contentId: string,
  destination: PackageDestination,
) =>
  publication.contentId === contentId &&
  publication.channel.toLowerCase() === destination.channel.toLowerCase() &&
  accountKey(publication.account) === accountKey(destination.account);
const localParts = (value: Date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(value);
  const get = (key: string) => parts.find((part) => part.type === key)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
};
const localInput = (value: string) =>
  value && Number.isFinite(Date.parse(value)) ? localParts(new Date(value)) : '';
/** Interpret entered wall time in São Paulo, regardless of the browser's timezone. */
function saoPauloInstant(value: string) {
  const target = Date.parse(`${value}Z`);
  if (!Number.isFinite(target)) throw new Error('Informe uma data e um horário válidos.');
  let time = target;
  for (let pass = 0; pass < 3; pass++)
    time += target - Date.parse(`${localParts(new Date(time))}Z`);
  return new Date(time).toISOString();
}
function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={`field ${wide ? 'wide' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}
function Evidence({ source, evidence }: { source: string; evidence: string }) {
  return source || evidence ? (
    <details className="dist-evidence">
      <summary>Fonte e evidência</summary>
      {source && (
        <p>
          <strong>Fonte:</strong> {source}
        </p>
      )}
      {evidence && <p className="dist-prewrap">{evidence}</p>}
    </details>
  ) : null;
}
function Dialog({
  title,
  busy,
  onClose,
  children,
}: {
  title: string;
  busy: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    ref.current?.showModal();
    return () => ref.current?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="dist-dialog"
      aria-labelledby={id}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
    >
      <div className="dist-dialog-heading">
        <h2 id={id}>{title}</h2>
        <button
          type="button"
          aria-label="Fechar formulário de distribuição"
          disabled={busy}
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>
      {children}
    </dialog>
  );
}

export function DistributionWorkspace({ campaignId, initialPackageId, data, refresh }: Props) {
  const [workspace, setWorkspace] = useState<Workspace>();
  const [selectedId, setSelectedId] = useState('');
  const [editor, setEditor] = useState<Editor>();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [formState, setFormState] = useState<DestinationDistribution['state']>('nao_solicitada');
  const [queueToken, setQueueToken] = useState(0);
  const sequence = useRef(0);
  const appliedPackage = useRef<string | undefined>(undefined);
  const endpoint = `/api/campaigns/${encodeURIComponent(campaignId)}/distribution`;
  async function load() {
    const request = ++sequence.current;
    const response = await fetch(endpoint);
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível consultar a distribuição.');
    if (request !== sequence.current) return;
    const value = result as Workspace;
    setWorkspace(value);
    setSelectedId((current) =>
      value.packages.some((pkg) => pkg.id === current)
        ? current
        : initialPackageId && value.packages.some((pkg) => pkg.id === initialPackageId)
          ? initialPackageId
          : ([...value.packages]
              .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
              .find((pkg) => pkg.status === 'aprovada')?.id ??
            [...value.packages].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.id ??
            ''),
    );
  }
  useEffect(() => {
    setLoading(true);
    setWorkspace(undefined);
    setSelectedId('');
    setEditor(undefined);
    setError('');
    setNotice('');
    appliedPackage.current = undefined;
    void load()
      .catch((reason) => setError(reason.message))
      .finally(() => setLoading(false));
    return () => {
      sequence.current++;
    };
  }, [campaignId]);
  useEffect(() => {
    if (!initialPackageId) {
      appliedPackage.current = undefined;
      return;
    }
    if (
      appliedPackage.current !== initialPackageId &&
      workspace?.packages.some((pkg) => pkg.id === initialPackageId)
    ) {
      appliedPackage.current = initialPackageId;
      setSelectedId(initialPackageId);
      setEditor(undefined);
      setError('');
      setNotice('');
    }
  }, [initialPackageId, workspace]);
  async function reload() {
    setLoading(true);
    setError('');
    setQueueToken((value) => value + 1);
    try {
      await load();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setLoading(false);
    }
  }
  async function save(path: string, value: unknown, message: string) {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
        body: JSON.stringify(value),
      });
      const result = await response.json();
      if (!response.ok) {
        if (response.status === 409) {
          setEditor(undefined);
          await load();
        }
        throw new Error(result.error || 'Não foi possível salvar o registro.');
      }
      setEditor(undefined);
      await Promise.all([load(), refresh()]);
      setQueueToken((value) => value + 1);
      setNotice(message);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const packages = [...(workspace?.packages ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  const pkg = packages.find((value) => value.id === selectedId);
  const destinations = pkg?.structuredDestinations ?? [];
  const records = workspace?.destinations.filter((item) => item.packageId === pkg?.id) ?? [];
  const requested = records.filter((item) => item.state !== 'nao_solicitada');
  const confirmed = records.filter((item) => item.state === 'confirmada');
  const titleFor = (item: PublicationPackage) => {
    const content = data.contents.find((content) => content.id === item.contentId);
    return content
      ? `${content.flgId ? `${content.flgId} · ` : ''}${content.title}`
      : 'Conteúdo indisponível';
  };
  const canEdit = Boolean(data.permissions.edit && pkg?.status === 'aprovada');
  function open(value: Editor) {
    setError('');
    setNotice('');
    setEditor(value);
    setFormState(
      pkg?.status === 'substituida' && value.item.state === 'desconhecido'
        ? 'cancelada'
        : value.item.state === 'em_envio'
          ? 'desconhecido'
          : value.item.state,
    );
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editor || !pkg || busy) return;
    const form = new FormData(event.currentTarget);
    const base = `${endpoint}/${encodeURIComponent(pkg.id)}/${encodeURIComponent(editor.destination.id)}`;
    const source = text(form, 'source'),
      evidence = text(form, 'evidence');
    if (editor.kind === 'collaboration') {
      void save(
        `${base}/collaborations`,
        {
          version: editor.collaboration.version,
          profile: editor.collaboration.profile,
          status: text(form, 'status'),
          source,
          evidence,
        },
        `Colaboração de ${editor.collaboration.profile} atualizada.`,
      );
      return;
    }
    const executor = text(form, 'executor');
    try {
      if (editor.kind === 'confirm') {
        const date = text(form, 'date'),
          time = text(form, 'time');
        void save(
          `${base}/confirm`,
          {
            version: editor.item.version,
            date,
            ...(form.has('exactTime') && time
              ? { publishedAt: saoPauloInstant(`${date}T${time}`) }
              : {}),
            url: text(form, 'url'),
            ...(text(form, 'publicationId') ? { publicationId: text(form, 'publicationId') } : {}),
            evidence,
            source,
            executor,
          },
          'Publicação confirmada neste destino. Colaboradores continuam separados.',
        );
      } else {
        const state = text(form, 'state'),
          scheduledAt = text(form, 'scheduledAt');
        void save(
          base,
          {
            version: editor.item.version,
            state,
            executor,
            source,
            evidence,
            scheduledAt:
              state.startsWith('agendada_') && scheduledAt ? saoPauloInstant(scheduledAt) : '',
            providerScheduleId:
              state === 'agendada_provedor' ? text(form, 'providerScheduleId') : '',
          },
          'Estado do destino registrado.',
        );
      }
    } catch (reason) {
      setError((reason as Error).message);
    }
  }
  return (
    <section className="dist-workspace" aria-labelledby="dist-heading">
      <div className="dist-heading">
        <div>
          <h2 id="dist-heading">Destinos e colaboração</h2>
          <p>Acompanhe cada conta do pacote aprovado e registre o que aconteceu na publicação.</p>
        </div>
        <button onClick={() => void reload()} disabled={loading || busy}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Atualizar destinos
        </button>
      </div>
      {!editor && error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {!workspace ? (
        <div className="dist-empty" role="status">
          {loading && <Loader2 className="spin" size={22} />}
          {loading
            ? 'Carregando destinos…'
            : 'Destinos ainda não disponíveis. Atualize para consultar os registros.'}
        </div>
      ) : !packages.length ? (
        <div className="dist-empty">
          <FileCheck2 size={24} />
          <div>
            <strong>Prepare o primeiro pacote</strong>
            <p>
              Na aba Materiais e aprovação, reúna a mídia, a legenda e as contas de destino para
              revisão.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="dist-package-picker">
            <Field label="Pacote de publicação">
              <select
                value={selectedId}
                onChange={(event) => {
                  setSelectedId(event.target.value);
                  setError('');
                  setNotice('');
                }}
                disabled={busy}
              >
                {packages.map((item) => (
                  <option key={item.id} value={item.id}>
                    {titleFor(item)} · revisão {item.revision} · {packageLabels[item.status]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {pkg && (
            <>
              <div className="dist-package-overview panel">
                <div>
                  <span className="dist-kicker">
                    REVISÃO {pkg.revision} · {packageLabels[pkg.status]}
                  </span>
                  <h3>{titleFor(pkg)}</h3>
                  <p>
                    {pkg.date
                      ? `Data planejada: ${dayLabel(pkg.date)}${pkg.plannedTime ? ` às ${pkg.plannedTime} · São Paulo` : ''}`
                      : 'Data planejada não definida'}
                  </p>
                  {pkg.decidedAt && (
                    <p className="dist-fine">
                      Decisão de {pkg.decidedBy || 'aprovador'} em {stamp(pkg.decidedAt)}.
                    </p>
                  )}
                </div>
                {destinations.length > 0 && (
                  <div className="dist-progress">
                    <strong>
                      {requested.length
                        ? `${confirmed.length} de ${requested.length}`
                        : 'Nenhum solicitado'}
                    </strong>
                    <span>
                      {!requested.length
                        ? 'Destinos aguardando registro'
                        : requested.length === 1
                          ? 'destino solicitado confirmado'
                          : 'destinos solicitados confirmados'}
                    </span>
                    <small>
                      {destinations.length}{' '}
                      {destinations.length === 1 ? 'conta no pacote' : 'contas no pacote'}
                    </small>
                  </div>
                )}
                <details className="dist-package-caption">
                  <summary>Legenda desta revisão</summary>
                  <p className="dist-prewrap">{pkg.caption || 'Sem legenda'}</p>
                  {pkg.caption && (
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(pkg.caption);
                          setNotice(
                            'Legenda copiada. Registre a confirmação após conferir a publicação no destino.',
                          );
                        } catch {
                          setError(
                            'Não foi possível copiar automaticamente. Selecione o texto da legenda.',
                          );
                        }
                      }}
                    >
                      <ClipboardCopy size={14} />
                      Copiar legenda
                    </button>
                  )}
                  <a
                    href="https://business.facebook.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ArrowUpRight size={14} /> Abrir Meta Business Suite
                  </a>
                </details>
              </div>
              {pkg.status !== 'aprovada' && (
                <p className="dist-note">
                  {pkg.status === 'substituida'
                    ? 'Esta revisão foi substituída. Seus registros ficam preservados. Resultados desconhecidos e respostas de colaboradores em publicações confirmadas podem ser documentados com evidência. Novos planejamentos exigem uma revisão aprovada vigente.'
                    : 'A distribuição fica disponível após a aprovação deste pacote. A decisão acontece na aba Materiais e aprovação.'}
                </p>
              )}
              {!destinations.length ? (
                <div className="dist-empty">
                  <FileCheck2 size={22} />
                  <div>
                    <strong>Este pacote usa o registro anterior de destinos</strong>
                    <p>
                      A decisão original está preservada. Para acompanhar contas e colaboradores
                      individualmente, crie uma nova revisão com destinos estruturados e envie para
                      aprovação.
                    </p>
                    <p className="dist-fine">
                      Destinos preservados: {pkg.destinations.join(' · ') || 'Não informados'}.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <PublishQueue
                    campaignId={campaignId}
                    packageId={pkg.id}
                    csrf={data.csrf}
                    permissions={data.permissions}
                    reloadToken={queueToken}
                    onChanged={() => Promise.all([load(), refresh()])}
                  />
                  <p className="dist-note">
                    Planejar no FLG registra uma intenção. Agendamento no provedor e publicação
                    confirmada exigem evidência. Colaboradores acompanham a mesma mídia e não são
                    novas publicações.
                  </p>
                  <div className="dist-destinations">
                    {destinations.map((destination) => {
                      const item = records.find(
                        (record) => record.destinationId === destination.id,
                      );
                      if (!item)
                        return (
                          <article className="dist-destination panel" key={destination.id}>
                            <h3>
                              {destination.channel} · {destination.account}
                            </h3>
                            <p role="alert">
                              Registro indisponível. Atualize os destinos para tentar novamente.
                            </p>
                          </article>
                        );
                      const publication = workspace?.publications.find(
                        (value) => value.id === item.publicationId,
                      );
                      const publishedAt =
                        item.publishedAt || publication?.publishedAt || publication?.date || '';
                      const precision = item.precision || publication?.precision;
                      // Espelho de um envio da fila simulada: o link aponta para um endereço fictício.
                      const simulated = Boolean(
                        item.mode === 'api' ||
                        (publication && publicationOrigin(publication).simulated),
                      );
                      const url = simulated
                        ? undefined
                        : safeUrl(item.url || publication?.url || '');
                      const simulatedUrl = simulated ? item.url || publication?.url || '' : '';
                      // Envio da API não terminal é dono do destino: registro manual é recusado.
                      const queueOwned = item.mode === 'api' && item.state !== 'cancelada';
                      const collaborators =
                        workspace?.collaborations.filter(
                          (value) =>
                            value.packageId === pkg.id && value.destinationId === destination.id,
                        ) ?? [];
                      const earlierPublications =
                        workspace?.publications.filter((publication) =>
                          sameDestination(publication, pkg.contentId, destination),
                        ) ?? [];
                      const canHistoricalCollaboration = Boolean(
                        data.permissions.edit &&
                        pkg.status === 'substituida' &&
                        item.state === 'confirmada' &&
                        item.packageHash === pkg.hash,
                      );
                      return (
                        <article className="dist-destination panel" key={destination.id}>
                          <div className="dist-destination-heading">
                            <div>
                              <span className="dist-kicker">{destination.channel}</span>
                              <h3>{destination.account}</h3>
                            </div>
                            <span className={`dist-status ${item.state}`}>
                              {distributionLabels[item.state]}
                            </span>
                          </div>
                          {item.state === 'confirmada' && (
                            <div className="dist-confirmed">
                              <CheckCheck size={18} />
                              <div>
                                <strong>
                                  {simulated
                                    ? 'Publicação simulada (API)'
                                    : 'Publicação registrada'}
                                </strong>
                                <p>
                                  {precision === 'day'
                                    ? `${dayLabel(publishedAt)} · horário não informado`
                                    : `${stamp(publishedAt)} · São Paulo`}
                                </p>
                                {url && (
                                  <a href={url} target="_blank" rel="noreferrer">
                                    Conferir publicação <ArrowUpRight size={13} />
                                  </a>
                                )}
                                {simulated && (
                                  <p className="dist-fine">
                                    Confirmada pelo gateway simulado: nada foi publicado de verdade.
                                    {simulatedUrl && ` Endereço simulado: ${simulatedUrl}`}
                                  </p>
                                )}
                                {!url && !simulated && (
                                  <p className="dist-fine">
                                    Sem link preservado; consulte a evidência do registro.
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {item.scheduledAt && (
                            <p className="dist-scheduled">
                              <CalendarDays size={14} />
                              {item.state === 'agendada_provedor'
                                ? 'Agendamento confirmado no provedor'
                                : 'Horário planejado'}
                              : {stamp(item.scheduledAt)} · São Paulo
                            </p>
                          )}
                          {item.providerScheduleId && (
                            <p className="dist-fine">
                              Identificador do agendamento: {item.providerScheduleId}
                            </p>
                          )}
                          {item.executor && (
                            <p className="dist-fine">Executor informado: {item.executor}</p>
                          )}
                          <Evidence source={item.source} evidence={item.evidence} />
                          {item.state === 'desconhecido' && !queueOwned && (
                            <p className="dist-unknown">
                              Confira a publicação no provedor e registre a evidência antes de
                              resolver este resultado.
                            </p>
                          )}
                          {item.state !== 'confirmada' && earlierPublications.length > 0 && (
                            <div className="dist-previous">
                              <strong>Há publicação registrada nesta conta</strong>
                              <p>
                                Confira se corresponde a esta revisão antes de registrar outra. A
                                confirmação pode reutilizar o registro existente.
                              </p>
                              <details>
                                <summary>
                                  Consultar{' '}
                                  {earlierPublications.length === 1
                                    ? 'publicação anterior'
                                    : `${earlierPublications.length} publicações anteriores`}
                                </summary>
                                {earlierPublications.map((publication) => (
                                  <p key={publication.id}>
                                    {dayLabel(publication.date)} ·{' '}
                                    {publicationOrigin(publication).label}
                                    {!publicationOrigin(publication).simulated &&
                                      safeUrl(publication.url) && (
                                        <>
                                          {' '}
                                          ·{' '}
                                          <a
                                            href={safeUrl(publication.url)}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Abrir publicação <ArrowUpRight size={12} />
                                          </a>
                                        </>
                                      )}
                                  </p>
                                ))}
                              </details>
                            </div>
                          )}
                          {queueOwned && (
                            <p className="dist-fine">
                              Este destino está na fila de publicação simulada. Acompanhe e aja pela
                              fila; o registro manual volta a valer se o envio for cancelado.
                            </p>
                          )}
                          {(canEdit ||
                            (data.permissions.edit &&
                              pkg.status === 'substituida' &&
                              item.state === 'desconhecido')) &&
                            !queueOwned &&
                            item.state !== 'confirmada' && (
                              <div className="dist-actions">
                                <button onClick={() => open({ kind: 'state', item, destination })}>
                                  {pkg.status === 'substituida'
                                    ? 'Documentar cancelamento'
                                    : 'Atualizar estado'}
                                </button>
                                <button
                                  className="primary"
                                  onClick={() => open({ kind: 'confirm', item, destination })}
                                >
                                  {item.state === 'desconhecido'
                                    ? 'Reconciliar publicação'
                                    : 'Registrar publicação'}
                                </button>
                              </div>
                            )}
                          <div className="dist-collaborations">
                            <h4>
                              <Users size={15} />
                              Colaboradores planejados
                            </h4>
                            {collaborators.length ? (
                              <ul>
                                {collaborators.map((collaboration) => (
                                  <li key={collaboration.id}>
                                    <div className="dist-collaboration-heading">
                                      <strong>{collaboration.profile}</strong>
                                      <span className={`dist-status ${collaboration.status}`}>
                                        {collaborationLabels[collaboration.status]}
                                      </span>
                                    </div>
                                    <Evidence
                                      source={collaboration.source}
                                      evidence={collaboration.evidence}
                                    />
                                    {(canEdit || canHistoricalCollaboration) && (
                                      <button
                                        className="text-button"
                                        onClick={() =>
                                          open({
                                            kind: 'collaboration',
                                            item,
                                            destination,
                                            collaboration,
                                          })
                                        }
                                        aria-label={`Atualizar colaboração de ${collaboration.profile} em ${destination.account}`}
                                      >
                                        Atualizar colaboração
                                      </button>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="dist-fine">
                                Nenhum perfil colaborador previsto neste destino.
                              </p>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
      {editor && pkg && (
        <Dialog
          title={
            editor.kind === 'confirm'
              ? 'Registrar publicação no destino'
              : editor.kind === 'collaboration'
                ? 'Atualizar colaboração'
                : 'Atualizar estado do destino'
          }
          busy={busy}
          onClose={() => setEditor(undefined)}
        >
          <p className="dist-dialog-context">
            {editor.destination.channel} · {editor.destination.account}
            {editor.kind === 'collaboration' ? ` · ${editor.collaboration.profile}` : ''}
          </p>
          <form
            onSubmit={submit}
            key={`${editor.kind}:${editor.item.id}:${editor.kind === 'collaboration' ? editor.collaboration.id : ''}`}
          >
            {editor.kind === 'state' ? (
              <StateFields item={editor.item} pkg={pkg} state={formState} setState={setFormState} />
            ) : editor.kind === 'confirm' ? (
              <ConfirmationFields
                story={pkg.contentFormat === 'Stories'}
                publications={
                  workspace?.publications.filter((publication) =>
                    sameDestination(publication, pkg.contentId, editor.destination),
                  ) ?? []
                }
                today={data.today}
              />
            ) : (
              <Field label="Estado da colaboração">
                <select
                  name="status"
                  required
                  defaultValue={
                    pkg.status !== 'substituida' ||
                    observedCollaborationStates.includes(editor.collaboration.status)
                      ? editor.collaboration.status
                      : ''
                  }
                >
                  {pkg.status === 'substituida' && (
                    <option value="">Selecione o estado observado</option>
                  )}
                  {Object.entries(collaborationLabels)
                    .filter(
                      ([value]) =>
                        pkg.status !== 'substituida' || observedCollaborationStates.includes(value),
                    )
                    .map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                </select>
              </Field>
            )}
            {editor.kind !== 'collaboration' && (
              <Field label="Executor informado">
                <input
                  name="executor"
                  required={
                    editor.kind !== 'state' || !['nao_solicitada', 'pronta'].includes(formState)
                  }
                  maxLength={250}
                  defaultValue={editor.item.executor}
                  placeholder="Quem conduziu a ocorrência neste destino"
                />
              </Field>
            )}
            <Field label="Fonte da conferência">
              <input
                name="source"
                required={
                  editor.kind !== 'state' || !['nao_solicitada', 'pronta'].includes(formState)
                }
                minLength={3}
                maxLength={250}
                defaultValue={editor.kind === 'collaboration' ? editor.collaboration.source : ''}
                placeholder="Ex.: conferência no perfil ou captura no Business Suite"
              />
            </Field>
            <Field label="Evidência do registro" wide>
              <textarea
                name="evidence"
                required={
                  editor.kind !== 'state' || !['nao_solicitada', 'pronta'].includes(formState)
                }
                minLength={3}
                maxLength={12000}
                rows={4}
                placeholder={
                  editor.kind === 'collaboration'
                    ? 'Descreva o que comprova o estado deste perfil. Convite enviado não confirma aceite.'
                    : 'Descreva o que foi conferido, por quem e a referência ou captura disponível.'
                }
              />
            </Field>
            {editor.kind === 'collaboration' && (
              <p className="dist-fine">
                Aceite exige evidência deste perfil. Este registro não cria outra publicação.
                {pkg.status === 'substituida' &&
                  ' Nesta revisão, registre somente o estado observado na publicação já confirmada.'}
              </p>
            )}
            {error && (
              <p className="notice error" role="alert">
                {error}
              </p>
            )}
            <div className="dist-form-actions">
              <button type="button" disabled={busy} onClick={() => setEditor(undefined)}>
                Cancelar
              </button>
              <button type="submit" className="primary" disabled={busy}>
                {busy && <Loader2 size={14} className="spin" />}
                {editor.kind === 'confirm' ? 'Confirmar registro de publicação' : 'Salvar registro'}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </section>
  );
}

function StateFields({
  item,
  pkg,
  state,
  setState,
}: {
  item: DestinationDistribution;
  pkg: PublicationPackage;
  state: DestinationDistribution['state'];
  setState: (state: DestinationDistribution['state']) => void;
}) {
  const states = Object.entries(distributionLabels).filter(
    ([value]) =>
      !['confirmada', 'em_envio'].includes(value) &&
      (pkg.status !== 'substituida' || value === 'cancelada') &&
      (item.state !== 'desconhecido' || ['desconhecido', 'cancelada'].includes(value)) &&
      (item.state === 'nao_solicitada' || value !== 'nao_solicitada') &&
      (!value.startsWith('agendada_') || Boolean(pkg.date && pkg.plannedTime)),
  );
  return (
    <>
      <Field label="Estado do destino">
        <select
          name="state"
          value={state}
          onChange={(event) => setState(event.target.value as DestinationDistribution['state'])}
        >
          {states.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      {state.startsWith('agendada_') && (
        <Field label="Data e hora planejadas (São Paulo)">
          <input
            type="datetime-local"
            name="scheduledAt"
            step="1"
            required
            defaultValue={
              localInput(item.scheduledAt) ||
              (pkg.date && pkg.plannedTime ? `${pkg.date}T${pkg.plannedTime}:00` : '')
            }
            readOnly
          />
        </Field>
      )}
      {!pkg.plannedTime && (
        <p className="dist-fine">
          Para registrar um agendamento, a revisão aprovada precisa incluir data e horário. A
          confirmação de uma publicação real usa a data efetiva.
        </p>
      )}
      {item.state === 'desconhecido' && (
        <p className="dist-fine">
          Documente o cancelamento ou feche este formulário e use “Reconciliar publicação” após
          conferir o resultado.
        </p>
      )}
      {state === 'agendada_provedor' && (
        <>
          <Field label="Identificador do agendamento no provedor">
            <input
              name="providerScheduleId"
              required
              maxLength={250}
              defaultValue={item.providerScheduleId}
            />
          </Field>
          <p className="dist-fine">
            Use este estado quando o provedor confirmar o agendamento e registre a evidência abaixo.
          </p>
        </>
      )}
    </>
  );
}
function ConfirmationFields({
  story,
  publications,
  today,
}: {
  story: boolean;
  publications: Publication[];
  today: string;
}) {
  const [exact, setExact] = useState(false);
  const [publicationId, setPublicationId] = useState('');
  const publication = publications.find((item) => item.id === publicationId);
  return (
    <>
      <p className="dist-note">
        Registre uma publicação já conferida neste perfil. Esta ação salva a confirmação no FLG.
      </p>
      {!!publications.length && (
        <Field label="Publicação já registrada">
          <select
            name="publicationId"
            value={publicationId}
            onChange={(event) => {
              setPublicationId(event.target.value);
              const selected = publications.find((item) => item.id === event.target.value);
              setExact(Boolean(selected?.precision === 'second' && selected.publishedAt));
            }}
          >
            <option value="">Registrar uma ocorrência ainda não cadastrada</option>
            {publications.map((item) => (
              <option key={item.id} value={item.id}>
                {dayLabel(item.date)} · {item.url || 'Sem link preservado'} ·{' '}
                {item.source === 'meta'
                  ? 'Meta'
                  : item.source === 'api'
                    ? publicationOrigin(item).label
                    : 'Manual'}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Data da publicação">
        <input
          key={`date:${publicationId}`}
          type="date"
          name="date"
          required
          max={today}
          defaultValue={publication?.date ?? ''}
          readOnly={!!publication}
        />
      </Field>
      <label className="dist-check">
        <input
          type="checkbox"
          name="exactTime"
          checked={exact}
          disabled={!!publication}
          onChange={(event) => setExact(event.target.checked)}
        />
        Tenho o horário exato da publicação
      </label>
      {exact && (
        <Field label="Horário da publicação (São Paulo)">
          <input
            key={`time:${publicationId}`}
            type="time"
            name="time"
            step="1"
            required
            defaultValue={
              publication?.publishedAt ? localInput(publication.publishedAt).slice(11) : ''
            }
            readOnly={!!publication}
          />
        </Field>
      )}
      <Field label={story ? 'Link HTTPS do Story, quando preservado' : 'Link HTTPS da publicação'}>
        <input
          key={`url:${publicationId}`}
          type="url"
          name="url"
          required={!story}
          placeholder="https://"
          defaultValue={publication?.url ?? ''}
          readOnly={!!publication}
        />
      </Field>
      {story && (
        <p className="dist-fine">
          Se o Story expirou, registre a evidência verificável da publicação, mesmo sem um link
          preservado.
        </p>
      )}
      {publication && (
        <p className="dist-fine">
          A fonte e a precisão do registro existente serão preservadas. Confirme apenas se ele
          corresponde a este pacote.
        </p>
      )}
    </>
  );
}
